import { useEffect, useState } from 'react';
import { fetchMyStorefront, getStorefrontPermits, uploadPermitServiceAuth } from '../../../services/appServices';
import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Card, Divider, IconButton, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import LoadingComponent from '../../components/LoadingComponent';
import { theme } from '../../../theme';
import { Accordion, AccordionSummary, AccordionDetails, Checkbox } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ApplyEventListingAuth } from '../../../services/eventServices';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useNavigate } from 'react-router-dom';
import { normalize, sleep } from '../../../utils/helperUtils';
import StripePayment from '../../components/StripePaymentComponent';
import InfoIcon from '@mui/icons-material/Info';

function ApplyEventDialogComponent({ open, onClose, eventInfo }) {
    const [storefront, setStorefront] = useState(null);
    const [loading, setLoading] = useState(false);
    const [inDialogloading, setInDialogLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [selectedPermitId, setSelectedPermitId] = useState(""); // must be string or number
    const [selectedBoothType, setSelectedBoothType] = useState(null);
    const { login, logout } = useAuth();
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
    const [description, setDescription] = useState("")
    const [existingPermits, setExistingPermits] = useState([])

    const [error, setError] = useState(null);

    const showSnackbar = useSnackbar();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (!open) return;

            setLoading(true);
            try {
                // Step 1: Fetch storefront
                const storefrontRes = await fetchMyStorefront(login, logout);
                if (storefrontRes.success) {
                    const storefrontData = storefrontRes.data;
                    setStorefront(storefrontData);

                    // Step 2: Only fetch permits if permit_required is true
                    if (eventInfo.permit_required) {
                        const permitsRes = await getStorefrontPermits(storefrontData.slug, login, logout);
                        setExistingPermits(permitsRes.success ? permitsRes.data : []);
                    } else {
                        setExistingPermits([]); // Clear existing permits if not required
                    }
                } else {
                    setStorefront(null);
                    setExistingPermits([]);
                }
            } catch (error) {
                console.error("Error during fetch:", error);
                setStorefront(null);
                setExistingPermits([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open]);

    const onDescriptionDialogClose = () => {
        setDescriptionDialogOpen(false)
        setUploadedFile(null)
        setSelectedPermitId("")
        setDescription("")
        setInDialogLoading(false)
    }

    const handlePermitUpload = async () => {

        if (!uploadedFile || !description) {
            console.warn("Missing file or description.");
            return;
        }

        setInDialogLoading(true);
        try {

            const response = await uploadPermitServiceAuth(
                {
                    file: uploadedFile,
                    description: description
                },
                storefront?.slug,
                login,
                logout
            );


            if (!response.success) {
                setError(response.error || "Something went wrong while applying");
            } else {

                const permitsResponse = await getStorefrontPermits(storefront?.slug, login, logout);
                if (response.success) {
                    setExistingPermits(permitsResponse.data);
                } else {
                    setExistingPermits([]);
                }

                setTimeout(() => {
                    onDescriptionDialogClose();
                    showSnackbar("🎉 Permit added successfully!", "success");
                    setInDialogLoading(false)
                }, 3000);

            }
        } catch (err) {
            setError(err.message || "Unexpected error while submitting application.");
        }
    }
    const handlePermitChange = (e) => {
        const value = e.target.value;
        if (value === "new_upload") {
            // Reset value temporarily so Select doesn't try to keep invalid value
            setSelectedPermitId("");
            document.getElementById("hidden-permit-upload-input")?.click();
        } else {
            const selected = existingPermits?.find(p => p.id === value);
            setUploadedFile(selected);
            setSelectedPermitId(value);
        }
    };
    const handleSubmit = async () => {
        setLoading(true);
        try {
            const boothObj = JSON.parse(selectedBoothType);
            const response = await ApplyEventListingAuth(
                eventInfo?.slug,
                {
                    booth_type: boothObj.booth_type_id,
                    ...(eventInfo?.permit_required && { permit_file: uploadedFile?.id }),
                },
                login,
                logout
            );

            if (!response.success) {
                setError(response.error || "Something went wrong while applying");
                onClose();
            } else {
                await sleep(3000);
                setLoading(false);
                setShowPayment(true);
            }
        } catch (err) {
            const normalizedMessage = normalize(err.message);
            const normalizedTarget = normalize(
                "You’ve already applied to this event with this storefront"
            );

            if (normalizedMessage.includes(normalizedTarget)) {
                // Gracefully continue
                await sleep(3000);
                setLoading(false);
                setShowPayment(true);
            } else {
                setError(err.message || "Unexpected error while submitting application.");
                onClose();
            }
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 3000);
        }
    };

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogContent>
                    <LoadingComponent fullScreen={false} textColor='black' />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                {!storefront ? (
                    <>
                        <DialogTitle>It's not us, it's you </DialogTitle>
                        <DialogContent>
                            <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
                                <Typography>
                                    🚫 No storefront found! Log in or create one now to this event 🎪
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="primary">Close</Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogTitle>
                            Apply to{" "}
                            <Typography
                                component="span"
                                variant="h6"
                                fontWeight={700}
                                sx={{
                                    display: "inline",
                                    letterSpacing: 0.5,
                                    color: theme.colors.events.primaryDark,
                                }}
                            >
                                {eventInfo?.name || 'this Event'}
                            </Typography>
                        </DialogTitle>
                        <Divider sx={{ mx: 3, }} />
                        <DialogContent>
                            <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                                <Box>
                                    {storefront && (
                                        showPayment ?
                                            <>
                                                <StripePayment slug={eventInfo?.slug} booth={selectedBoothType} />
                                            </> :
                                            <>
                                                <Typography variant='h6' gutterBottom>Your Sidewalk Vendor Profile</Typography>
                                                <Card
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 5,
                                                        px: 2,
                                                        py: 1.5,
                                                        bgcolor: theme.colors.events.secondary,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 2,
                                                    }}
                                                    elevation={1}
                                                >
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        {storefront.logo && (
                                                            <Box
                                                                component="img"
                                                                src={storefront.logo}
                                                                alt="Storefront Logo"
                                                                sx={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover' }}
                                                            />
                                                        )}
                                                        <Typography variant="h6" fontWeight={600}>
                                                            {storefront.name}
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        component="a"
                                                        href={`/${storefront.slug}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ color: 'black' }}
                                                    >
                                                        <OpenInNewIcon />
                                                    </IconButton>
                                                </Card>
                                                {/* Booth Types Selection */}
                                                {eventInfo?.booth_types?.length > 0 && (
                                                    <Box mt={3}>
                                                        <Typography variant="h6" gutterBottom>Select Booth Type</Typography>
                                                        <RadioGroup
                                                            name="boothType"
                                                            value={selectedBoothType}
                                                            onChange={(e) => {
                                                                setSelectedBoothType(e.target.value)
                                                            }}
                                                        >
                                                            <Box sx={{ overflowX: "auto", width: "100%" }}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        gap: 2,
                                                                        py: 1,
                                                                        px: 0.5,
                                                                        ml: 1,
                                                                        flexWrap: "nowrap",
                                                                        minWidth: "max-content",
                                                                    }}
                                                                >
                                                                    {eventInfo?.booth_types?.map((booth) => (
                                                                        <Box key={booth.booth_type_id} sx={{ display: "inline-block" }}>
                                                                            <FormControlLabel
                                                                                value={JSON.stringify(booth)}
                                                                                control={<Radio sx={{ color: theme.colors.events.primary }} />}
                                                                                label={
                                                                                    <Box>
                                                                                        <Typography variant="subtitle1" fontWeight={600}>{booth.name} — ${booth.total_cost}</Typography>
                                                                                        <Typography variant="body2" color="text.secondary">{booth.description}</Typography>
                                                                                        {/* <Typography variant="caption" color="text.secondary">Available: {booth.quantity_available}</Typography> */}
                                                                                    </Box>
                                                                                }
                                                                                sx={{
                                                                                    minWidth: 200,
                                                                                    border: '1px solid #ddd',
                                                                                    borderRadius: 2,
                                                                                    p: 1,
                                                                                    bgcolor: '#fafafa',
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        </RadioGroup>
                                                    </Box>
                                                )}
                                                {eventInfo?.permit_required &&
                                                <Box mt={3}>
                                                    <Box display="flex" alignItems="flex-start" gap={0.5}>
                                                        <Typography variant="h6">Permit Documents</Typography>
                                                        <Tooltip
                                                            title="Permits can be viewed in 'My Storefront'. Tap ☰ menu to get started!"
                                                            enterTouchDelay={0}
                                                            leaveTouchDelay={3000}
                                                            placement="bottom"
                                                            arrow
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        bgcolor: theme.colors.primary, // change to your desired color
                                                                        color: 'white', // text color
                                                                        borderRadius: 3,
                                                                        fontSize: '0.85rem',
                                                                        px: 2,
                                                                        py: 1,
                                                                        whiteSpace: 'normal',
                                                                        maxWidth: 250,
                                                                    }
                                                                },
                                                                arrow: {
                                                                    sx: {
                                                                        color: theme.colors.primary // make the arrow match bg
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <IconButton size="small">
                                                                <InfoIcon sx={{ color: theme.colors.events.primaryDark }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>          
                                                    {eventInfo?.permit_description && <Typography variant="subtitle1" gutterBottom>{eventInfo?.permit_description}</Typography>}
                                              
                                                    <Select
                                                        displayEmpty
                                                        fullWidth
                                                        value={selectedPermitId}
                                                        onChange={handlePermitChange}
                                                        renderValue={(selected) => {
                                                            if (!selectedPermitId) return "Select or upload a permit";
                                                            const permit = existingPermits?.find(p => p.id === selectedPermitId);
                                                            return permit ? `Selected: ${permit.description}` : "Select or upload a permit";
                                                        }}
                                                        sx={{ borderRadius: 5, bgcolor: "#f9f9f9" }}
                                                    >
                                                        {existingPermits?.map((permit) => (
                                                            <MenuItem key={permit.id} value={permit.id}>
                                                                {permit.description}
                                                            </MenuItem>
                                                        ))}
                                                        <Divider />
                                                        <MenuItem value="new_upload" sx={{ color: theme.colors.events.primary }}>
                                                            + Upload New File
                                                        </MenuItem>
                                                    </Select>

                                                    {/* Hidden input for file upload */}
                                                    <input
                                                        type="file"
                                                        id="hidden-permit-upload-input"
                                                        hidden
                                                        accept=".pdf,image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                setUploadedFile(file);
                                                                setTimeout(() => setDescriptionDialogOpen(true), 0); // open description dialog
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                                }
                                                <Divider sx={{ mt: 2 }} />
                                                <Box mt={2}>
                                                    <Accordion
                                                        elevation={0}
                                                        square={true}
                                                        sx={{
                                                            border: "0.5px solid grey",
                                                            borderRadius: 5,
                                                            overflow: "hidden",
                                                            "&:before": { display: "none" } // Removes the default divider line
                                                        }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon className='manage-card-icon-circle' sx={{ color: theme.colors.events.primary }} />}>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                Terms and Conditions
                                                            </Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Typography variant="body2">
                                                                {eventInfo?.tnc
                                                                    ? eventInfo.tnc
                                                                    : "By applying to this event, you agree to comply with all event policies, vendor guidelines, and city regulations. Any violation may result in removal from the event without a refund. Ensure your documents are valid and you follow setup timings."
                                                                }
                                                            </Typography>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                    <Box mt={1}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={acceptedTerms}
                                                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                                                    sx={{
                                                                        color: theme.colors.events.primary,
                                                                        borderRadius: "50%",
                                                                        '& .MuiSvgIcon-root': {
                                                                            borderRadius: "50%", // Ensures icon is rounded
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2">
                                                                    I accept the Terms and Conditions
                                                                </Typography>
                                                            }
                                                        />
                                                    </Box>
                                                </Box>
                                            </>
                                    )}
                                </Box>
                            </Box>
                        </DialogContent>
                        {!showPayment &&
                            <DialogActions>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        px: 2,
                                        pb: 2,
                                        gap: 2,
                                    }}
                                >
                                    <Button
                                        onClick={onClose}
                                        variant="outlined"
                                        color="secondary"
                                        sx={{ borderRadius: 3, textTransform: "capitalize" }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => handleSubmit()}
                                        variant="contained"
                                        disabled={!acceptedTerms || !selectedBoothType || eventInfo?.permit_required && !uploadedFile}
                                        sx={{
                                            borderRadius: 3,
                                            textTransform: "capitalize",
                                            fontWeight: 600,
                                            boxShadow: 2,
                                            px: 4,
                                            bgcolor: theme.colors.events.primary,
                                        }}
                                    >
                                        Submit Application
                                    </Button>
                                </Box>
                            </DialogActions>
                        }
                    </>
                )}
            </Dialog>
            {error && (
                <Dialog open={!!error} onClose={() => setError(null)}>
                    <DialogTitle>Error</DialogTitle>
                    <DialogContent>
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setError(null)} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <Dialog open={descriptionDialogOpen} onClose={() => setDescriptionDialogOpen(false)} fullWidth>

                {inDialogloading ?
                    <LoadingComponent fullScreen={false} textColor='black' />
                    : <>
                        <DialogTitle>Add Optional Description</DialogTitle>
                        <DialogContent>
                            <TextField
                                required
                                autoFocus
                                margin="dense"
                                label="Description"
                                fullWidth
                                variant="outlined"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                error={description.trim() === ""}
                                helperText={description.trim() === "" ? "Description is required" : ""}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 4,
                                    }
                                }}
                            />
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={onDescriptionDialogClose}>Cancel</Button>
                            <Button
                                sx={{ borderRadius: 3 }}
                                disableElevation
                                onClick={handlePermitUpload}
                                variant="contained"
                                disabled={description.trim() === ""}
                            >
                                Submit
                            </Button>
                        </DialogActions>
                    </>}
            </Dialog>
        </>
    );
}

export default ApplyEventDialogComponent;